import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';
import { MicrosoftTeamsUserSearchResource } from '../helpdesk/shared/resources/microsoft-teams-user-search.resource';
import { Microsoft365GroupResource } from '../helpdesk/shared/resources/microsoft-365-group.resource';
import { MicrosoftCallQueueSearchResource } from '../helpdesk/shared/resources/microsoft-call-queue-search.resource';
import { MicrosoftAutoAttendantsSearchResource } from '../helpdesk/shared/resources/microsoft-auto-attendants-search.resource';
import { ZiroSelectOption } from '../forms/fields/select/ziro-select.component';
import { AccountType, VoiceAppViewEntityType } from '../shared/models/generated/ziroModels';

const SEARCH_LIMIT = 250;

@Injectable()
export class TeamsAutoAttendantsDestinationsSearchService {
  private _msTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _ms365GroupResource = inject(Microsoft365GroupResource);
  private _msCallQueueSearchResource = inject(MicrosoftCallQueueSearchResource);
  private _msAutoAttendantsSearchResource = inject(MicrosoftAutoAttendantsSearchResource);

  searchUsers(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';
    return this._msTeamsUserSearchResource.search(tenantId, query, null, SEARCH_LIMIT, AccountType.USER).pipe(
      map((users) =>
        users.map((user) => {
          const hasPhoneNumber = !!user.lineUri && user.lineUri.trim().length > 0;
          return {
            label: user.ref?.displayName ?? user.userPrincipalName,
            value: { ...user, displayName: user.ref?.displayName ?? user.userPrincipalName },
            secondaryContent: user.userPrincipalName ?? '',
            disabled: !hasPhoneNumber,
            disabledTooltip: !hasPhoneNumber
              ? 'tkey;admin.teams_voice_management.teams_auto_attendants.call_flow.destination.buttons.user.no_phone_number.tooltip'
              : undefined,
          };
        }),
      ),
    );
  }

  searchResourceAccounts(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';
    return this._msTeamsUserSearchResource
      .search(tenantId, query, null, SEARCH_LIMIT, AccountType.RESOURCE_ACCOUNT)
      .pipe(
        map((accounts) =>
          accounts.map((account) => ({
            label: account.ref?.displayName ?? account.userPrincipalName,
            value: { ...account, displayName: account.ref?.displayName ?? account.userPrincipalName },
            secondaryContent: account.userPrincipalName ?? '',
          })),
        ),
      );
  }

  searchVoicemailGroups(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';

    return this._ms365GroupResource.search(tenantId, query).pipe(
      map((groups) =>
        groups.map((group) => ({
          label: group.name,
          value: { ...group, displayName: group.name },
          secondaryContent: group.groupType ?? '',
        })),
      ),
    );
  }

  searchVoiceApps(tenantId: string, term: string): Observable<ZiroSelectOption[]> {
    const query = term?.trim() ?? '';

    return forkJoin([
      this._msCallQueueSearchResource.search(tenantId, query),
      this._msAutoAttendantsSearchResource.search(tenantId, query),
    ]).pipe(
      map(([callQueues, autoAttendants]) => [
        ...callQueues.map((cq) => ({
          label: cq.name,
          value: {
            id: cq.callQueueId,
            guid: cq.callQueueId,
            displayName: cq.name,
            type: VoiceAppViewEntityType.CALL_QUEUE,
          },
          group: 'Call Queue',
        })),
        ...autoAttendants.map((aa) => ({
          label: aa.name,
          value: { id: aa.id, guid: aa.id, displayName: aa.name, type: VoiceAppViewEntityType.AUTO_ATTENDANT },
          group: 'Auto Attendant',
        })),
      ]),
    );
  }
}
